import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '@/composables/useApi'

interface User {
  id: string
  email: string
  name: string
  role: 'elder' | 'family'
  avatar?: string
  bindCode?: string
  phone?: string
  birthday?: string
  emergencyContact?: string
  plan?: 'free' | 'premium'
  subscriptionStatus?: 'active' | 'inactive' | 'cancelled' | 'expired'
  subscriptionExpiresAt?: string | null
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const isElder = computed(() => user.value?.role === 'elder')
  const isFamily = computed(() => user.value?.role === 'family')
  const isSubscribed = computed(() => {
    if (!user.value) return false
    if (user.value.plan !== 'premium') return false
    if (user.value.subscriptionStatus !== 'active') return false
    if (user.value.subscriptionExpiresAt) {
      return new Date(user.value.subscriptionExpiresAt).getTime() > Date.now()
    }
    return true
  })

  function loadFromStorage() {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    if (storedToken) token.value = storedToken
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        user.value = null
      }
    }
  }

  async function login(email: string, password: string) {
    const { post } = useApi()
    const result = await post<{ token: string; refreshToken: string; user: User }>('/auth/login', { email, password }, false)
    if (result.data) {
      token.value = result.data.token
      user.value = result.data.user
      localStorage.setItem('token', result.data.token)
      localStorage.setItem('refreshToken', result.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(result.data.user))
    }
    return result
  }

  async function register(payload: {
    email: string
    password: string
    name: string
    role: 'elder' | 'family'
    bindCode?: string
  }) {
    const { post } = useApi()
    const result = await post<{ token: string; refreshToken: string; user: User }>('/auth/register', payload, false)
    if (result.data) {
      token.value = result.data.token
      user.value = result.data.user
      localStorage.setItem('token', result.data.token)
      localStorage.setItem('refreshToken', result.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(result.data.user))
    }
    return result
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  async function fetchProfile() {
    const { get } = useApi()
    const result = await get<User>('/user/profile')
    if (result.data) {
      user.value = result.data
      localStorage.setItem('user', JSON.stringify(result.data))
    }
    return result
  }

  async function updateProfile(data: Partial<User>) {
    const { put } = useApi()
    const result = await put<User>('/user/profile', data)
    if (result.data) {
      user.value = result.data
      localStorage.setItem('user', JSON.stringify(result.data))
    }
    return result
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const { put } = useApi()
    return put<{ success: boolean }>('/user/password', { currentPassword, newPassword })
  }

  async function getInviteCode() {
    const { get } = useApi()
    const result = await get<{ inviteCode: string }>('/user/invite-code')
    if (result.data && user.value) {
      user.value.bindCode = result.data.inviteCode
      localStorage.setItem('user', JSON.stringify(user.value))
    }
    return result
  }

  async function deleteAccount() {
    const { del } = useApi()
    const result = await del('/auth/account')
    if (!result.error) {
      logout()
    }
    return result
  }

  loadFromStorage()

  return {
    user,
    token,
    isAuthenticated,
    isElder,
    isFamily,
    isSubscribed,
    login,
    register,
    logout,
    fetchProfile,
    updateProfile,
    changePassword,
    getInviteCode,
    deleteAccount,
    loadFromStorage,
  }
})
